<template>
  <div>
    <base-title-bar-scroll-page ref="container" :back="true" title="康复知识" @onBackClick="goBack">
      <div class="article-detail-container">
        <div v-if="loading" class="loading-container">
          <van-skeleton :row="8" />
        </div>

        <div v-else-if="article" class="article-content">
          <!-- 文章头部 -->
          <div class="article-header">
            <h1 class="article-title">{{ article.title }}</h1>
            <div class="article-meta">
              <span class="article-category">{{ article.category }}</span>
              <span class="article-date">{{ article.date }}</span>
            </div>
          </div>

          <!-- 文章摘要 -->
          <div class="article-summary" v-if="article.summary">
            <div class="summary-title">文章摘要</div>
            <p>{{ article.summary }}</p>
          </div>

          <!-- 文章内容 -->
          <div class="article-body" v-html="article.content"></div>

          <!-- 相关推荐 -->
          <div class="related-articles" v-if="relatedArticles.length > 0">
            <div class="related-title">相关阅读</div>
            <div class="related-list">
              <div class="related-item"
                   v-for="related in relatedArticles"
                   :key="related.id"
                   @click="goToRelated(related.id)">
                <div class="related-image" v-if="related.image">
                  <img :src="related.image" :alt="related.title">
                </div>
                <div class="related-content">
                  <h4>{{ related.title }}</h4>
                  <p>{{ related.summary }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-else class="error-container">
          <van-empty image="error" description="文章加载失败" />
          <van-button type="primary" @click="loadArticle">重新加载</van-button>
        </div>
      </div>
    </base-title-bar-scroll-page>
  </div>
</template>

<script>
import BaseTitleBarScrollPage from '@/h5/doucan/components/BaseTitleBarScrollPage.vue'
import { Skeleton, Empty } from 'vant'
import SpineScienceDao from '@/h5/doucan/network/SpineScienceDao'

export default {
  name: 'ShunyiArticleDetailPage',
  components: {
    BaseTitleBarScrollPage,
    'van-skeleton': Skeleton,
    'van-empty': Empty
  },
  data () {
    return {
      loading: true,
      article: null,
      articleId: '',
      relatedArticles: [],
      categoryKey: '',
      categoryName: '康复知识'
    }
  },
  mounted () {
    this.articleId = this.$route.query.id
    this.categoryKey = this.$route.query.category || ''
    this.categoryName = this.$route.query.categoryName || '康复知识'
    this.loadArticle()
    this.hideLoading()
  },
  methods: {
    hideLoading () {
      this.$refs.container.stopLoading()
    },
    async loadArticle () {
      this.loading = true
      try {
        const article = await SpineScienceDao.loadArticle(this.articleId)
        this.article = this.normalizeArticle(article)
        try {
          await this.loadRelatedArticles()
        } catch (relatedError) {
          console.warn('加载相关文章失败', relatedError)
          this.relatedArticles = []
        }
      } catch (error) {
        console.error('加载文章详情失败:', error)
        this.article = null
        this.relatedArticles = []
      } finally {
        this.loading = false
      }
    },
    normalizeArticle (data) {
      return {
        id: data.id,
        title: data.title,
        category: this.categoryName,
        date: this.formatDate(data.gmtModified || data.gmtCreate),
        image: data.coverUrl || '',
        summary: data.summary || '',
        content: data.content || ''
      }
    },
    formatDate (value) {
      if (!value) return ''
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return String(value).slice(0, 10)
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
    },
    async loadRelatedArticles () {
      if (!this.categoryKey || !this.article) return
      const articles = await SpineScienceDao.loadArticles(this.categoryKey)
      this.relatedArticles = (articles || [])
        .filter(item => item.id !== this.article.id)
        .slice(0, 3)
        .map(item => ({
          id: item.id,
          title: item.title,
          summary: item.summary || '',
          image: item.coverUrl || ''
        }))
    },
    goBack () {
      this.$router.go(-1)
    },
    goToRelated (id) {
      this.$router.push({
        path: '/shunyi/article/detail',
        query: { id, category: this.categoryKey, categoryName: this.categoryName }
      })
    }
  },
  watch: {
    '$route.query.id' (newId) {
      if (newId !== this.articleId) {
        this.articleId = newId
        this.categoryKey = this.$route.query.category || ''
        this.categoryName = this.$route.query.categoryName || '康复知识'
        this.loadArticle()
      }
    }
  }
}
</script>

<style scoped>
.article-detail-container {
  background: white;
  min-height: 100vh;
  max-width: 100%;
  overflow-x: hidden;
}

.loading-container {
  padding: 16px;
}

.article-content {
  padding: 0;
  max-width: 100%;
  overflow-x: hidden;
  background: #fff;
}

.article-header {
  padding: 20px 16px;
  background: #fff;
  color: #222;
}

.article-title {
  font-size: 24px;
  font-weight: 700;
  line-height: 1.3;
  margin: 0 0 12px 0;
}

.article-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
  color: #666;
}

.article-category {
  background: #f2f3f5;
  padding: 4px 8px;
  border-radius: 12px;
  font-weight: 500;
}

.article-image {
  width: 100%;
  height: 240px;
  overflow: hidden;
  box-sizing: border-box;
}

.article-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.article-summary {
  padding: 16px;
  background: #fff;
  margin: 0;
}

.summary-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
}

.article-summary p {
  font-size: 14px;
  line-height: 1.6;
  color: #666;
  margin: 0;
}

.article-body {
  padding: 20px 16px;
  font-size: 16px;
  line-height: 1.8;
  color: #333;
  background: #fff;
  max-width: 100%;
  box-sizing: border-box;
  overflow-wrap: anywhere;
  word-break: break-word;
}

:deep(.article-body *) {
  max-width: 100%;
  box-sizing: border-box;
}

/* 富文本内容由 v-html 渲染，使用 deep 选择器保证标题、段落等样式生效。 */
:deep(.article-body h1),
:deep(.article-body h2) {
  font-size: 20px;
  font-weight: 600;
  color: #1976D2;
  margin: 24px 0 16px 0;
  padding-bottom: 8px;
  border-bottom: 2px solid #E3F2FD;
  line-height: 1.5;
}

:deep(.article-body h1) {
  font-size: 22px;
}

:deep(.article-body h3) {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 20px 0 12px 0;
  line-height: 1.5;
}

:deep(.article-body h4),
:deep(.article-body h5),
:deep(.article-body h6) {
  font-size: 16px;
  font-weight: 600;
  color: #555;
  margin: 16px 0 10px 0;
  line-height: 1.5;
}

:deep(.article-body p) {
  margin: 12px 0;
  text-align: justify;
}

/* 兼容 Quill 工具栏设置的左中右/两端对齐样式，避免被正文默认样式覆盖。 */
:deep(.article-body .ql-align-left) {
  text-align: left !important;
}

:deep(.article-body .ql-align-center) {
  text-align: center !important;
}

:deep(.article-body .ql-align-right) {
  text-align: right !important;
}

:deep(.article-body .ql-align-justify) {
  text-align: justify !important;
}

:deep(.article-body ul),
:deep(.article-body ol) {
  margin: 12px 0;
  padding-left: 24px;
}

:deep(.article-body li) {
  margin: 8px 0;
  line-height: 1.8;
}

:deep(.article-body strong) {
  color: #1976D2;
  font-weight: 600;
}

:deep(.article-body img) {
  display: block;
  max-width: 100% !important;
  width: auto !important;
  height: auto !important;
  margin: 16px auto;
  object-fit: contain;
}

:deep(.article-body figure) {
  width: auto !important;
  margin: 16px 0;
}

:deep(.article-body video),
:deep(.article-body iframe),
:deep(.article-body canvas) {
  display: block;
  width: 100% !important;
  max-width: 100% !important;
  min-height: 180px;
  aspect-ratio: 16 / 9;
  border: 0;
}

:deep(.article-body video) {
  height: auto !important;
  background: #000;
  object-fit: contain;
}

:deep(.article-body table) {
  display: block;
  max-width: 100% !important;
  overflow-x: auto;
  border-collapse: collapse;
  -webkit-overflow-scrolling: touch;
}

:deep(.article-body th),
:deep(.article-body td) {
  min-width: 72px;
  padding: 8px;
  border: 1px solid #e5e5e5;
  overflow-wrap: anywhere;
}

:deep(.article-body pre),
:deep(.article-body code) {
  max-width: 100% !important;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  word-break: break-word;
}

:deep(.article-body pre) {
  padding: 12px;
  background: #f6f8fa;
  border-radius: 4px;
}

:deep(.article-body a) {
  overflow-wrap: anywhere;
  word-break: break-all;
}

:deep(.article-body .conclusion),
:deep(.article-body .expert-reminder),
:deep(.article-body .action-tips) {
  background: #E3F2FD;
  padding: 16px;
  border-radius: 8px;
  margin: 20px 0;
  border-left: 4px solid #1976D2;
}

:deep(.article-body .conclusion h2),
:deep(.article-body .expert-reminder h2),
:deep(.article-body .action-tips h3) {
  color: #1976D2;
  margin-top: 0;
  font-size: 18px;
  border-bottom: none;
  padding-bottom: 0;
}

.related-articles {
  padding: 20px 16px;
  background: #fff;
  margin-top: 30px;
}

.related-title {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin-bottom: 16px;
  padding-left: 12px;
  border-left: 4px solid #1976D2;
}

.related-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.related-item {
  display: flex;
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  cursor: pointer;
  transition: transform 0.2s;
}

.related-item:active {
  transform: scale(0.98);
}

.related-image {
  width: 100px;
  height: 100px;
  flex-shrink: 0;
  overflow: hidden;
  background: #f0f0f0;
}

.related-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.related-content {
  flex: 1;
  padding: 12px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.related-content h4 {
  font-size: 15px;
  font-weight: 600;
  color: #333;
  margin: 0 0 8px 0;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.related-content p {
  font-size: 13px;
  color: #666;
  line-height: 1.5;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.error-container {
  padding: 60px 16px;
  text-align: center;
}

/* 响应式设计 */
@media (max-width: 375px) {
  .article-header {
    padding: 16px 12px;
  }

  .article-title {
    font-size: 20px;
  }

  .article-body {
    padding: 16px 12px;
    font-size: 15px;
  }

  .article-body h2 {
    font-size: 18px;
  }

  .related-image {
    width: 80px;
    height: 80px;
  }
}

@media (min-width: 414px) {
  .article-image {
    height: 260px;
  }

  .article-title {
    font-size: 26px;
  }
}
</style>
